/**
 * Right-side scrolling script panel — Pro Tools cue-list aesthetic.
 *
 * White paper-like background, black body type, monospace line numbers in the
 * gutter, and the active cue highlighted with PT yellow (#fff3a3) plus a
 * 1px black accent on the left. Status icons read like classic SF Symbol
 * glyphs.
 *
 * Behaviour matches the previous studio version:
 *   - Auto-scrolls to keep the current cue centered when activeCueId changes
 *   - Click any of my cues to jump (parent handles state)
 *   - Header shows scene label + record-full-scene button
 */
import { useEffect, useMemo, useRef } from 'react'
import type { ActorCue, SceneRef } from '../../lib/api/cues'
import { cx } from './cx'

export interface ScriptPanelProps {
  scenes: SceneRef[]
  cues: ActorCue[]
  activeCueId: string | null
  onJumpToCue: (cueId: string) => void
  onRecordFullScene: () => void
  onCustomRange: () => void
  pickupQueue: Set<string>
  /** Locale for header strings only — cue text is rendered as-is (source language). */
  locale?: 'zh' | 'en'
}

export function ScriptPanel({
  scenes,
  cues,
  activeCueId,
  onJumpToCue,
  onRecordFullScene,
  onCustomRange,
  pickupQueue,
  locale = 'en',
}: ScriptPanelProps) {
  const activeRef = useRef<HTMLDivElement | null>(null)

  const sceneLabel = (n: number): string => {
    const s = scenes.find((x) => x.scene_number === n)
    return s ? s.label : `Scene ${n}`
  }

  // Auto-scroll active cue into view (centered).
  useEffect(() => {
    if (!activeRef.current) return
    activeRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [activeCueId])

  const activeCue = cues.find((c) => c.cue_id === activeCueId)
  const activeScene = activeCue?.scene_number
  const activeSceneLabel = activeScene !== undefined ? sceneLabel(activeScene) : ''
  const activeMineCount = cues.filter((c) => c.scene_number === activeScene && c.is_mine).length
  const activeTotalCount = cues.filter((c) => c.scene_number === activeScene).length

  const groups: Array<{ scene: number; cues: ActorCue[] }> = []
  let current: { scene: number; cues: ActorCue[] } | null = null
  for (const c of cues) {
    if (!current || current.scene !== c.scene_number) {
      current = { scene: c.scene_number, cues: [] }
      groups.push(current)
    }
    current.cues.push(c)
  }

  const numberByCueId = useMemo(() => {
    const m = new Map<string, number>()
    cues.forEach((c, i) => m.set(c.cue_id, i + 1))
    return m
  }, [cues])

  return (
    <div className="flex h-full flex-col bg-pt-wave-bg text-pt-text" data-tour="script-panel">
      {/* Header — PT chrome panel. */}
      <div className="border-b border-pt-border bg-pt-panel">
        <div className="flex items-start justify-between px-4 pt-3 pb-2">
          <div className="space-y-0.5">
            <div className="pt-mono text-[10px] uppercase tracking-[0.18em] text-pt-text-soft">
              {locale === 'zh' ? '场景' : 'Scene'} · {activeScene ?? '—'}
            </div>
            <div className="text-[14px] font-semibold text-pt-text">{activeSceneLabel}</div>
            <div className="pt-mono text-[11px] text-pt-text-soft">
              {activeTotalCount} cues · <span className="text-pt-wave">{activeMineCount} mine</span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 px-4 pb-3">
          <button
            type="button"
            onClick={onRecordFullScene}
            className="pt-btn flex items-center gap-1.5 px-2.5 py-1 text-[11px]"
          >
            Record full scene <span className="pt-kbd">G</span>
          </button>
          <button
            type="button"
            onClick={onCustomRange}
            title="Shift-click two of your cues to define a range"
            className="pt-btn px-2.5 py-1 text-[11px]"
          >
            Custom range
          </button>
        </div>
      </div>

      {/* Scrollable cue list — paper-white background. */}
      <div className="scroll-y flex-1 overflow-y-auto bg-pt-wave-bg px-2 py-3">
        {groups.map((g, gi) => (
          <div key={`scene-${g.scene}-${gi}`}>
            {gi > 0 && (
              <div className="mx-3 my-3 flex items-center gap-3">
                <div className="h-px flex-1 bg-pt-border-soft" />
                <span className="pt-mono text-[10px] uppercase tracking-[0.18em] text-pt-text-soft">
                  {sceneLabel(g.scene)}
                </span>
                <div className="h-px flex-1 bg-pt-border-soft" />
              </div>
            )}
            {g.cues.map((c) => {
              const isActive = c.cue_id === activeCueId
              const isApproved = c.is_mine && c.latest_take_status === 'approved'
              const isPickup = c.is_mine && pickupQueue.has(c.cue_id)
              const lineNo = numberByCueId.get(c.cue_id) ?? 0
              const lineNoText = lineNo.toString().padStart(3, '0')

              const gutterGlyph = isPickup ? '!' : isApproved ? '✓' : c.is_mine ? '●' : '○'
              const gutterColor = isPickup
                ? 'text-pt-rec'
                : isApproved
                  ? 'text-[#16a34a]'
                  : c.is_mine
                    ? 'text-pt-wave'
                    : 'text-pt-text-soft'

              return (
                <div
                  key={c.cue_id}
                  ref={isActive ? activeRef : null}
                  role={c.is_mine ? 'button' : undefined}
                  tabIndex={c.is_mine ? 0 : -1}
                  aria-current={isActive ? 'true' : undefined}
                  onClick={c.is_mine ? () => onJumpToCue(c.cue_id) : undefined}
                  onKeyDown={
                    c.is_mine
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            onJumpToCue(c.cue_id)
                          }
                        }
                      : undefined
                  }
                  className={cx(
                    'script-line group relative flex items-start gap-3 px-3 py-1.5 text-[13px] leading-[1.55]',
                    'transition-colors',
                    !c.is_mine && 'opacity-50 hover:opacity-70',
                    c.is_mine && isApproved && 'text-pt-text-soft',
                    c.is_mine && !isActive && !isApproved && 'text-pt-text hover:bg-pt-panel2',
                    c.is_mine && 'cursor-pointer',
                    isActive && 'bg-pt-highlight text-pt-text',
                  )}
                >
                  {/* Active accent bar — solid black, 1px (PT selection rule). */}
                  {isActive && (
                    <span
                      aria-hidden="true"
                      className="absolute left-0 top-0 h-full w-[2px] bg-pt-text"
                    />
                  )}
                  <span
                    className={cx(
                      'mt-0.5 pt-mono text-[10px] tabular-nums',
                      isActive ? 'text-pt-text' : 'text-pt-text-soft',
                      'select-none',
                    )}
                    aria-hidden="true"
                  >
                    {lineNoText}
                  </span>
                  <span
                    aria-hidden="true"
                    className={cx('mt-0.5 w-3 text-center pt-mono text-[11px]', gutterColor)}
                  >
                    {gutterGlyph}
                  </span>
                  <span className="flex-1">
                    <span
                      className={cx(
                        'mr-1 pt-mono text-[10px] uppercase tracking-wide',
                        c.is_mine ? 'text-pt-wave' : 'text-pt-text-soft',
                      )}
                    >
                      {c.character_code}
                    </span>
                    <span className={cx(isActive && 'font-semibold')}>{c.line_text}</span>
                    {isPickup && (
                      <span
                        className="ml-2 inline-flex items-center gap-1 rounded-sm border border-pt-text bg-pt-warn px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-pt-text"
                        title={c.pickup_note ?? 'Pickup requested'}
                      >
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-pt-rec" />
                        pickup
                      </span>
                    )}
                  </span>
                </div>
              )
            })}
          </div>
        ))}
      </div>

      {/* Footer legend — PT chrome strip. */}
      <div className="flex justify-between border-t border-pt-border bg-pt-panel px-4 py-2 pt-mono text-[10px] uppercase tracking-[0.15em] text-pt-text-soft">
        <span>
          <span className="text-pt-wave">●</span> mine
        </span>
        <span>
          <span className="text-pt-text-soft">○</span> other
        </span>
        <span>
          <span className="text-[#16a34a]">✓</span> done
        </span>
        <span>
          <span className="text-pt-rec">!</span> pickup
        </span>
      </div>
    </div>
  )
}
